import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Star, Check, X, Trash2, RefreshCw, ExternalLink, Eye, EyeOff, Heart } from "lucide-react";
import { Layout } from "../components/Layout";
import { AdminStripeSettings } from "../components/AdminStripeSettings";
import { useAdmin } from "../lib/admin";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { fetchDates, fetchFeatured } from "../lib/api";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "featured", label: "Featured dates" },
  { id: "donations", label: "Donations" },
  { id: "settings", label: "Stripe" },
];

const SUBMISSION_TABS = ["pending", "approved", "rejected"];

const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

export const AdminDashboardPage = () => {
  const { admin, loading, logout, authedClient } = useAdmin();
  const navigate = useNavigate();

  const [tab, setTab] = useState("pending");
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [allDates, setAllDates] = useState([]);
  const [donations, setDonations] = useState([]);
  const [donationSummary, setDonationSummary] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [actionError, setActionError] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [search, setSearch] = useState("");

  useDocumentMeta({
    title: "Admin Dashboard · Will Not Be Forgotten",
    description: "Moderate community submissions and curate featured dates in the Will Not Be Forgotten archive.",
  });

  // Redirect to login if not authed
  useEffect(() => {
    if (!loading && !admin) navigate("/admin", { replace: true });
  }, [loading, admin, navigate]);

  const refresh = useCallback(async () => {
    if (!admin) return;
    setLoadingList(true);
    setActionError("");
    try {
      const [statsRes, subsRes, featRes, allRes, donRes, sumRes] = await Promise.all([
        authedClient.get("/admin/stats"),
        authedClient.get("/admin/submissions"),
        fetchFeatured(),
        fetchDates({ limit: 1000 }),
        authedClient.get("/admin/donations"),
        authedClient.get("/admin/donations/summary"),
      ]);
      setStats(statsRes.data);
      setSubmissions(subsRes.data);
      setFeatured(featRes);
      setAllDates(allRes);
      setDonations(donRes.data);
      setDonationSummary(sumRes.data);
    } catch (e) {
      setActionError(e?.response?.data?.detail || e?.message || "Failed to load admin data.");
    } finally {
      setLoadingList(false);
    }
  }, [admin, authedClient]);

  useEffect(() => { refresh(); }, [refresh]);

  const filteredSubs = useMemo(() => {
    if (tab === "featured") return [];
    return submissions.filter((s) => (s.status || "pending") === tab);
  }, [submissions, tab]);

  const filteredDates = useMemo(() => {
    if (tab !== "featured") return [];
    const q = search.trim().toLowerCase();
    const list = allDates;
    if (!q) return list;
    return list.filter((d) =>
      d.title.toLowerCase().includes(q) ||
      (d.short_description || "").toLowerCase().includes(q) ||
      (d.categories || []).some((c) => c.includes(q))
    );
  }, [tab, allDates, search]);

  const flash = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(""), 1800);
  };

  const onApprove = async (id) => {
    try {
      await authedClient.post(`/admin/submissions/${id}/approve`, { note: null });
      flash("Submission approved.");
      refresh();
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Approve failed.");
    }
  };
  const onReject = async (id) => {
    const note = window.prompt("Add a brief rejection note (optional):", "");
    if (note === null) return;
    try {
      await authedClient.post(`/admin/submissions/${id}/reject`, { note });
      flash("Submission rejected.");
      refresh();
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Reject failed.");
    }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently? This cannot be undone.")) return;
    try {
      await authedClient.delete(`/admin/submissions/${id}`);
      flash("Submission deleted.");
      refresh();
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Delete failed.");
    }
  };
  const onToggleFeature = async (id) => {
    try {
      await authedClient.post(`/admin/dates/${id}/feature`);
      flash("Featured status toggled.");
      refresh();
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Feature toggle failed.");
    }
  };
  const onSetVisibility = async (id, patch) => {
    try {
      await authedClient.post(`/admin/donations/${id}/visibility`, patch);
      flash("Supporter wall updated.");
      refresh();
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Update failed.");
    }
  };
  const onDeleteDonation = async (id) => {
    if (!window.confirm("Permanently delete this supporter entry? This removes it from the wall and from your totals and cannot be undone.")) return;
    try {
      await authedClient.delete(`/admin/donations/${id}`);
      flash("Donation deleted.");
      refresh();
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Delete failed.");
    }
  };

  if (loading || !admin) {
    return (
      <Layout headerVariant="solid" hideFooter>
        <div className="shell" style={{ padding: "4rem 1.5rem" }}>
          <p style={{ color: "hsl(var(--muted))" }}>Loading admin…</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout headerVariant="solid" hideFooter>
      <section data-testid="admin-dashboard" className="section" style={{ paddingTop: "3rem", borderTop: "none" }}>
        <div className="shell">
          <header style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-end",
            flexWrap: "wrap", gap: "1rem", marginBottom: "2rem",
          }}>
            <div>
              <span className="section__eyebrow">Moderation</span>
              <h1 style={{ marginTop: 0, fontSize: "clamp(1.8rem, 3vw, 2.5rem)" }}>Archive dashboard</h1>
              <p style={{ marginTop: 6, color: "hsl(var(--muted))", fontSize: "0.9rem" }}>
                Signed in as <strong>{admin.email}</strong>
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button type="button" onClick={refresh} className="btn btn--ghost" data-testid="admin-refresh" disabled={loadingList}>
                <RefreshCw size={14} aria-hidden /> Refresh
              </button>
              <button type="button" onClick={() => { logout(); navigate("/admin", { replace: true }); }} className="btn btn--ghost" data-testid="admin-logout">
                <LogOut size={14} aria-hidden /> Sign out
              </button>
            </div>
          </header>

          {stats && (
            <div data-testid="admin-stats" style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "1rem",
              marginBottom: "2.5rem",
            }}>
              {[
                { l: "Pending", v: stats.pending_submissions },
                { l: "Approved", v: stats.approved_submissions },
                { l: "Rejected", v: stats.rejected_submissions },
                { l: "Total dates", v: stats.total_dates },
                { l: "Featured", v: stats.featured_dates },
                { l: "Donations", v: stats.paid_donations },
                { l: "Recurring", v: stats.recurring_donations },
              ].map((s) => (
                <div key={s.l} style={{
                  padding: "1rem 1.1rem",
                  background: "hsl(var(--bg-elev))",
                  border: "1px solid hsl(var(--line))",
                }}>
                  <div className="archival-figure" style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "1.7rem",
                    color: "hsl(var(--accent))",
                    fontWeight: 500,
                    lineHeight: 1,
                  }}>{s.v}</div>
                  <div style={{
                    fontSize: "0.7rem",
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "hsl(var(--muted))",
                    marginTop: 4,
                  }}>{s.l}</div>
                </div>
              ))}
            </div>
          )}

          <div role="tablist" aria-label="Admin sections" data-testid="admin-tabs" style={{
            display: "inline-flex", flexWrap: "wrap", gap: 6,
            border: "1px solid hsl(var(--line))",
            background: "hsl(var(--bg-elev))",
            padding: 4,
            borderRadius: 999,
            marginBottom: "1.25rem",
          }}>
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button key={t.id} role="tab" aria-selected={active} data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  style={{
                    padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: 500,
                    background: active ? "hsl(var(--accent))" : "transparent",
                    color: active ? "hsl(var(--accent-ink))" : "hsl(var(--ink))",
                    border: "none", borderRadius: 999, cursor: "pointer",
                  }}>
                  {t.label}
                </button>
              );
            })}
          </div>

          {actionMsg && <div role="status" data-testid="admin-flash" style={{
            padding: "0.7rem 0.9rem", marginBottom: 12,
            border: "1px solid hsl(var(--accent) / 0.5)",
            background: "hsl(var(--accent) / 0.08)",
            color: "hsl(var(--accent))",
            fontSize: "0.9rem",
          }}>{actionMsg}</div>}
          {actionError && <div role="alert" data-testid="admin-error" style={{
            padding: "0.7rem 0.9rem", marginBottom: 12,
            border: "1px solid hsl(var(--destructive) / 0.5)",
            background: "hsl(var(--destructive) / 0.08)",
            color: "hsl(var(--destructive))",
            fontSize: "0.9rem",
          }}>{actionError}</div>}

          {SUBMISSION_TABS.includes(tab) ? (
            <div data-testid="admin-submissions-list" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {loadingList ? (
                <p style={{ color: "hsl(var(--muted))" }}>Loading submissions…</p>
              ) : filteredSubs.length === 0 ? (
                <p style={{ color: "hsl(var(--muted))" }}>No {tab} submissions.</p>
              ) : filteredSubs.map((s) => (
                <article key={s.id} data-testid="admin-submission-row" style={{
                  padding: "1.25rem",
                  border: "1px solid hsl(var(--line))",
                  background: "hsl(var(--bg-elev))",
                }}>
                  <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
                    <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", margin: 0 }}>
                      {s.proposed_title}
                    </h3>
                    <span className="tag tag--accent">{s.status || "pending"}</span>
                  </header>
                  <div style={{ fontSize: "0.82rem", color: "hsl(var(--muted))", marginBottom: 10 }}>
                    {s.proposed_date_label && <span>{s.proposed_date_label} · </span>}
                    {s.proposed_category && <span>{s.proposed_category} · </span>}
                    Submitted {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                    {s.submitter_email ? <> by {s.submitter_email}</> : null}
                  </div>
                  <p style={{ fontSize: "0.95rem", lineHeight: 1.6, marginTop: 0 }}>{s.rationale}</p>
                  {s.sources && (
                    <p style={{ fontSize: "0.85rem", color: "hsl(var(--muted))", whiteSpace: "pre-wrap" }}>
                      <strong style={{ color: "hsl(var(--ink-soft))" }}>Sources:</strong> {s.sources}
                    </p>
                  )}
                  {s.review_note && (
                    <p style={{ fontSize: "0.85rem", color: "hsl(var(--muted))" }}>
                      <strong style={{ color: "hsl(var(--ink-soft))" }}>Review note:</strong> {s.review_note}
                    </p>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    {s.status !== "approved" && (
                      <button type="button" className="btn btn--primary" data-testid={`approve-${s.id}`} onClick={() => onApprove(s.id)}>
                        <Check size={14} aria-hidden /> Approve
                      </button>
                    )}
                    {s.status !== "rejected" && (
                      <button type="button" className="btn btn--ghost" data-testid={`reject-${s.id}`} onClick={() => onReject(s.id)}>
                        <X size={14} aria-hidden /> Reject
                      </button>
                    )}
                    <button type="button" className="btn btn--ghost" data-testid={`delete-${s.id}`} onClick={() => onDelete(s.id)}
                      style={{ borderColor: "hsl(var(--destructive) / 0.4)", color: "hsl(var(--destructive))" }}
                    >
                      <Trash2 size={14} aria-hidden /> Delete
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : tab === "featured" ? (
            <div data-testid="admin-featured-list">
              <input
                type="search"
                placeholder="Search dates by title, description, or category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-feature-search"
                style={{
                  width: "100%", maxWidth: 540,
                  padding: "0.65rem 0.85rem",
                  border: "1px solid hsl(var(--line))",
                  background: "hsl(var(--bg))",
                  color: "hsl(var(--ink))",
                  fontSize: "0.92rem",
                  marginBottom: "1rem",
                  borderRadius: 2,
                }}
              />
              <p style={{ fontSize: "0.85rem", color: "hsl(var(--muted))" }}>
                {featured.length} featured · {filteredDates.length} matches
              </p>
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10,
                marginTop: 8,
              }}>
                {filteredDates.slice(0, 100).map((d) => (
                  <article key={d.id} style={{
                    border: "1px solid hsl(var(--line))",
                    background: "hsl(var(--bg-elev))",
                    padding: "0.85rem 1rem",
                    display: "flex", flexDirection: "column", gap: 6,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <span style={{ fontFamily: "var(--font-display)", fontSize: "1rem", fontWeight: 500 }}>{d.title}</span>
                      <button type="button" data-testid={`feature-toggle-${d.id}`} onClick={() => onToggleFeature(d.id)} style={{
                        background: d.featured ? "hsl(var(--accent))" : "transparent",
                        color: d.featured ? "hsl(var(--accent-ink))" : "hsl(var(--accent))",
                        border: "1px solid hsl(var(--accent))",
                        padding: "0.25rem 0.55rem", fontSize: "0.72rem",
                        letterSpacing: "0.1em", textTransform: "uppercase",
                        cursor: "pointer", borderRadius: 999,
                        display: "inline-flex", alignItems: "center", gap: 4,
                      }}>
                        <Star size={12} aria-hidden /> {d.featured ? "Featured" : "Feature"}
                      </button>
                    </div>
                    <span style={{ fontSize: "0.78rem", color: "hsl(var(--muted))" }}>{d.date_label} · {d.recognition_status}</span>
                    <Link to={`/date/${d.id}`} target="_blank" rel="noopener" style={{
                      fontSize: "0.8rem", color: "hsl(var(--accent))",
                      display: "inline-flex", alignItems: "center", gap: 4,
                    }}>
                      View page <ExternalLink size={11} aria-hidden />
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          ) : tab === "donations" ? (
            <div data-testid="admin-donations-list" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {donationSummary && (
                <div data-testid="donation-summary" style={{
                  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "0.75rem", marginBottom: "0.5rem",
                }}>
                  {[
                    { l: "Total raised", v: fmtMoney(donationSummary.total_raised) },
                    { l: "This month", v: fmtMoney(donationSummary.this_month_raised) },
                    { l: "Gifts", v: donationSummary.count },
                    { l: "Recurring", v: donationSummary.recurring_count },
                    { l: "Avg gift", v: fmtMoney(donationSummary.average_gift) },
                    { l: "Est. monthly recurring", v: fmtMoney(donationSummary.estimated_monthly_recurring) },
                    { l: "Renewals", v: `${donationSummary.renewal_count} · ${fmtMoney(donationSummary.renewal_total)}` },
                  ].map((c) => (
                    <div key={c.l} style={{ padding: "0.9rem 1rem", background: "hsl(var(--bg-elev))", border: "1px solid hsl(var(--line))" }}>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", color: "hsl(var(--accent))", fontWeight: 500, lineHeight: 1 }}>{c.v}</div>
                      <div style={{ fontSize: "0.68rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "hsl(var(--muted))", marginTop: 4 }}>{c.l}</div>
                    </div>
                  ))}
                </div>
              )}
              {loadingList ? (
                <p style={{ color: "hsl(var(--muted))" }}>Loading donations…</p>
              ) : donations.length === 0 ? (
                <p style={{ color: "hsl(var(--muted))" }} data-testid="admin-donations-empty">No completed donations yet.</p>
              ) : donations.map((d) => (
                <article key={d.id} data-testid="admin-donation-row" style={{
                  padding: "1.1rem 1.25rem",
                  border: "1px solid hsl(var(--line))",
                  background: "hsl(var(--bg-elev))",
                  opacity: d.hidden ? 0.55 : 1,
                }}>
                  <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Heart size={15} aria-hidden style={{ color: "hsl(var(--accent))" }} />
                      <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", margin: 0 }}>
                        {d.donor_name || <span style={{ color: "hsl(var(--muted))", fontStyle: "italic" }}>No name given</span>}
                      </h3>
                      {d.anonymous && <span className="tag">Anonymous</span>}
                      <span className="tag tag--accent">{d.frequency || "once"}</span>
                      {d.hidden && <span className="tag" style={{ borderColor: "hsl(var(--destructive) / 0.4)", color: "hsl(var(--destructive))" }}>Hidden</span>}
                    </div>
                    <span style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", color: "hsl(var(--accent))", fontWeight: 500 }}>
                      {fmtMoney(d.amount)}
                    </span>
                  </header>
                  <div style={{ fontSize: "0.8rem", color: "hsl(var(--muted))", marginBottom: d.donor_message ? 8 : 0 }}>
                    {d.paid_at ? new Date(d.paid_at).toLocaleString() : ""} · amount {d.show_amount ? "shown" : "hidden"} on wall
                  </div>
                  {d.donor_message && (
                    <p style={{
                      fontSize: "0.92rem", lineHeight: 1.55, margin: "0 0 8px", fontStyle: "italic",
                      color: d.message_hidden ? "hsl(var(--muted))" : "hsl(var(--ink-soft))",
                      textDecoration: d.message_hidden ? "line-through" : "none",
                    }}>
                      “{d.donor_message}”
                    </p>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                    <button type="button" className="btn btn--ghost" data-testid={`donation-toggle-hidden-${d.id}`}
                      onClick={() => onSetVisibility(d.id, { hidden: !d.hidden })}>
                      {d.hidden ? <Eye size={14} aria-hidden /> : <EyeOff size={14} aria-hidden />}
                      {d.hidden ? "Show on wall" : "Hide from wall"}
                    </button>
                    {d.donor_message && (
                      <button type="button" className="btn btn--ghost" data-testid={`donation-toggle-message-${d.id}`}
                        onClick={() => onSetVisibility(d.id, { message_hidden: !d.message_hidden })}>
                        {d.message_hidden ? <Eye size={14} aria-hidden /> : <EyeOff size={14} aria-hidden />}
                        {d.message_hidden ? "Show message" : "Hide message"}
                      </button>
                    )}
                    <button type="button" className="btn btn--ghost" data-testid={`donation-delete-${d.id}`}
                      onClick={() => onDeleteDonation(d.id)}
                      style={{ borderColor: "hsl(var(--destructive) / 0.4)", color: "hsl(var(--destructive))" }}>
                      <Trash2 size={14} aria-hidden /> Delete
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div data-testid="admin-settings-panel">
              <AdminStripeSettings authedClient={authedClient} />
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};
